/**
 * A/B test registry.
 *
 * One entry per experiment the admin Analytics → Experiments tab should report.
 * The numbers themselves always come from PostHog (api/admin-experiments.js);
 * this file only declares what to measure and when a result is worth reading.
 *
 * Fields
 *  - id                stable key, also used in the API response
 *  - name              short human name for the card title
 *  - question          what the test is trying to answer, in plain English
 *  - surface           'web' (marketing site) or 'app' (React Native app)
 *  - status            'live' (emitting events) or 'planned' (not instrumented)
 *  - exposureEvent     PostHog event fired once per person shown the variant
 *  - conversionEvent   PostHog event fired when the variant achieved its goal
 *  - variantProperty   event property that carries the variant id
 *  - variants          [{ id, label }] — labels are what the UI shows
 *  - minPerArm         exposures every arm needs before a leader is highlighted
 *  - notes             honest caveats shown in the UI
 *
 * Nothing here is data. A planned experiment has no event names and must never
 * render numbers — the API returns `not_instrumented` for those.
 */

export const DEFAULT_MIN_PER_ARM = 1000;

export const EXPERIMENTS = [
  {
    id: "app_banner",
    name: "Download bar",
    question: "Which download bar gets the most app-store taps per impression?",
    surface: "web",
    status: "live",
    exposureEvent: "app_banner_viewed",
    conversionEvent: "download_app_clicked",
    variantProperty: "banner_variant",
    variants: [
      { id: "purple", label: "Purple curtain" },
      { id: "glass", label: "Glass" },
      { id: "dock", label: "Dark dock, neon edge" },
    ],
    minPerArm: DEFAULT_MIN_PER_ARM,
    notes:
      "Click-through is download_app_clicked ÷ app_banner_viewed for the same banner_variant. " +
      "docs/analytics/APP_BANNER_AB_TEST.md estimates ~10,000 impressions per arm to settle a " +
      "20% relative difference, so this gate is the minimum to look at, not a significance test.",
  },
  {
    id: "single_event_layout",
    name: "Single event page",
    question: "Which event page gets more sessions with a ticket or signup CTA tap?",
    surface: "web",
    status: "live",
    exposureEvent: "single_event_exposed",
    conversionEvent: "single_event_cta_clicked",
    variantProperty: "layout_variant",
    variants: [
      {
        id: "classic", label: "Current design (control)",
        hypothesis: "The existing page is the baseline for all four alternatives.",
      },
      {
        id: "express", label: "Express booking",
        hypothesis: "One compact booking panel and expandable details reduce the effort to choose a ticket.",
      },
      {
        id: "cinematic", label: "Cinematic poster",
        hypothesis: "Immersive artwork, editorial sections and a large artist lineup make the whole night feel more compelling.",
      },
      {
        id: "social", label: "Go together",
        hypothesis: "Real guestlist information and organizer context beside the ticket action help people feel confident joining.",
      },
      {
        id: "guide", label: "Night guide",
        hypothesis: "A compact practical summary and one numbered section navigation help people find the details they need before booking.",
      },
    ],
    minPerArm: DEFAULT_MIN_PER_ARM,
    notes:
      "20% assignment per design, kept in this browser. Exposure and the first primary CTA tap " +
      "are each counted once per browser page session per variant, across events. A full reload " +
      "starts a new session. Rate = CTA events ÷ exposure events; this measures intent, not unique " +
      "people or completed bookings. App-only, game, exclusive, sold-out, presale and expired events are " +
      "excluded. Preview traffic is excluded. The exposure gate is not a significance test.",
  },
  {
    id: "app_theme",
    name: "App light vs dark",
    question: "Does a light or dark app theme keep more people browsing?",
    surface: "app",
    status: "planned",
    exposureEvent: null,
    conversionEvent: null,
    variantProperty: "theme",
    variants: [
      { id: "light", label: "Light" },
      { id: "dark", label: "Dark" },
    ],
    minPerArm: DEFAULT_MIN_PER_ARM,
    notes:
      "The React Native app does not emit a theme event yet. It needs an exposure event " +
      "(for example app_theme_viewed with a `theme` property) and a conversion event before " +
      "this test can report anything.",
  },
];

/** Look up a single experiment by id. Returns null when it is not registered. */
export const getExperiment = (id) => EXPERIMENTS.find((experiment) => experiment.id === id) || null;
